/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { Breadcrumb, Button, Col, Row, Spin, Typography } from 'antd';
import { PlusOutlined, ScheduleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import MainLayout from '../components/common/MainLayout';
import HomeHeader from '../components/home/HomeHeader';
import MyPlotCard from '../components/home/MyPlotCard';
import PlanningCard from '../components/home/PlanningCard';
import HomeMiniCard from '../components/home/HomeMiniCard';
import { useListSavesQuery } from '../api/savesV2Api';
import { selectIsAuthenticated } from '../store/slices/authSlice';
import learnArt from '../assets/home/learn.webp';
import challengesArt from '../assets/home/challenges.webp';
import seasonsArt from '../assets/home/seasons.webp';

const { Paragraph } = Typography;

const styles = {
  page: css({ display: 'flex', flexDirection: 'column', gap: 16 }),
  miniGrid: css({ marginTop: 8 }),
  emptyFarmCta: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'flex-start',
    padding: 16,
  }),
};

export default function HomePage() {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data: freeSaves, isLoading: savesLoading } = useListSavesQuery(
    { type: 'free', status: 'active', limit: 20 },
    { skip: !isAuthenticated },
  );
  const freeSave = useMemo(
    () => (freeSaves || [])[0] || null,
    [freeSaves],
  );

  const openSavePath = async (path: (saveId: string) => string) => {
    if (freeSave) {
      navigate(path(freeSave.id));
      return;
    }
    navigate('/saves?type=free');
  };

  const goToSaveRoot = () => openSavePath((id) => `/saves/${id}`);
  const goToTasks = () => openSavePath((id) => `/saves/${id}/tasks`);
  const goToPlannerHub = () => openSavePath((id) => `/saves/${id}`);

  if (!isAuthenticated) {
    return null;
  }

  if (savesLoading) {
    return (
      <MainLayout breadcrumb={<Breadcrumb items={[{ title: 'Главная' }]} />}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  const hasSave = Boolean(freeSave);

  return (
    <MainLayout breadcrumb={<Breadcrumb items={[{ title: 'Главная' }]} />}>
      <div css={styles.page}>
        <HomeHeader />

        {hasSave ? (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={14}>
                <MyPlotCard
                  farmName={freeSave?.farmName || 'Мой участок'}
                  modeLabel="Свободный режим"
                  modeDescription="Ваш участок — ваши правила."
                  hasFarm={hasSave}
                  hasSave={hasSave}
                  loading={savesLoading}
                  onPrimaryClick={goToSaveRoot}
                  primaryLabel={hasSave ? 'Открыть участок' : 'Играть'}
                  primaryLoading={false}
                  actions={[
                    {
                      key: 'water',
                      icon: <ScheduleOutlined />,
                      title: 'Полив',
                      hint: 'Перейти к задачам полива',
                      onClick: goToTasks,
                    },
                    {
                      key: 'harvest',
                      icon: <ScheduleOutlined />,
                      title: 'Сбор урожая',
                      hint: 'Готовые к сбору грядки',
                      onClick: goToTasks,
                    },
                    {
                      key: 'greenhouse',
                      icon: <ScheduleOutlined />,
                      title: 'Проверить теплицу',
                      hint: 'Открыть мир / поле теплицы',
                      onClick: goToSaveRoot,
                    },
                  ]}
                />
              </Col>
              <Col xs={24} lg={10}>
                <PlanningCard
                  description="Планируйте посадки и развивайте участок."
                  onOpen={goToPlannerHub}
                  ctaLabel={hasSave ? 'Открыть планы' : 'Играть и планировать'}
                  loading={false}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]} css={styles.miniGrid}>
              <Col xs={24} sm={12} md={8}>
                <HomeMiniCard
                  tone="cream"
                  art={learnArt}
                  title="Обучение"
                  description="Полезные статьи, советы и гайды для фермеров."
                  badge="скоро"
                  disabled
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <HomeMiniCard
                  tone="lilac"
                  art={challengesArt}
                  title="Испытания"
                  description="Сценарии с целями: проверьте себя в тематических заданиях."
                  badge="бесплатно"
                  onClick={() => navigate('/scenarios')}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <HomeMiniCard
                  tone="pink"
                  art={seasonsArt}
                  title="Сезоны"
                  description="Общие задания для всех, официальный рейтинг из серверного реплея."
                  badge="скоро"
                  disabled
                />
              </Col>
            </Row>
          </>
        ) : (
          <div css={styles.emptyFarmCta}>
            <Paragraph>
              Добро пожаловать! Чтобы начать, создайте первый сейв с фермой, участком и погодой.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => navigate('/saves?type=free')}
            >
              Создать первый сейв
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
